import type { FC } from "react";
import { useState } from "react";

import { Button } from "@jti/core-components/components/Button";
import type { AddressStored } from "@jti/fe-common";
import { AddressSelectCard } from "@jti/ui/components/AddressSelectCard";
import { useFormContext } from "@jti/ui/components/Form";
import { useTranslations } from "@jti/ui/providers/useTranslations";

import { ActionButtons } from "../ActionButtons";
import { AddressForm } from "../AddressForm";

import styles from "./EditAddress.module.scss";

export type EditAddressProps = {
  addresses?: AddressStored[];
  onSelectExistingAddress?: (address: AddressStored) => void;
  onSaveNewAddress?: () => void;
  onCancelAddressSelections?: () => void;
};

type EditAddressView = "selectCard" | "form";

export const EditAddress: FC<EditAddressProps> = ({
  addresses = [],
  onSaveNewAddress,
  onSelectExistingAddress,
  onCancelAddressSelections,
}) => {
  const { __ } = useTranslations();
  const {
    formState: { isValid },
  } = useFormContext();
  const [selectedAddress, setSelectedAddress] = useState<AddressStored>(addresses[0]);
  const [view, setView] = useState<EditAddressView>("selectCard");

  const isFormVisible = view === "form";
  const isAddressCardsVisible = view === "selectCard";
  const primaryButtonLabel = isFormVisible
    ? __("loyalty.redeemDialog.saveNewAddressButtonText")
    : __("loyalty.redeemDialog.selectAddressButtonText");

  const handleChangeViewToForm = () => {
    setView("form");
  };

  const handleChangeViewToSelectCard = () => {
    setView("selectCard");
  };

  const handleConfirmNewAddress = () => {
    if (isAddressCardsVisible && selectedAddress) {
      onSelectExistingAddress?.(selectedAddress);
      return;
    }
    if (isFormVisible) {
      onSaveNewAddress?.();
    }
  };

  return (
    <>
      {isFormVisible && (
        <>
          <AddressForm />
          <Button variant="text" onClick={handleChangeViewToSelectCard}>
            {__("loyalty.redeemDialog.selectExistingAddressButtonText")}
          </Button>
        </>
      )}

      {isAddressCardsVisible && (
        <>
          <div className={styles.addressCardsWrapper} data-testid="address-cards-wrapper">
            {addresses.map((address) => {
              return (
                <AddressSelectCard
                  address={address}
                  onSelect={() => {
                    setSelectedAddress(address);
                  }}
                  isSelected={selectedAddress?.id === address.id}
                  key={address.id}
                />
              );
            })}
          </div>
          <Button variant="text" onClick={handleChangeViewToForm}>
            {__("loyalty.redeemDialog.enterNewAddressButtonText")}
          </Button>
        </>
      )}

      <ActionButtons
        onPrimaryButtonClick={handleConfirmNewAddress}
        onSecondaryButtonClick={onCancelAddressSelections}
        isPrimaryButtonDisabled={isFormVisible && !isValid}
        primaryButtonLabel={primaryButtonLabel}
        secondaryButtonLabel={__("loyalty.redeemDialog.cancelButtonText")}
      />
    </>
  );
};
